import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useFormFields, formatStatus } from 'helpers';
import {
  Row,
  Col,
  Alerts,
  ButtonAction,
  Modal,
  Container,
  PageContent,
  Pagination,
  Table,
  FiltersControls,
  Spinner,
  FiltersCustom,
} from 'components';
import {
  loadMaPeopleListCreator,
  setMaPeopleListSortCreator,
  setMaPeopleListPageCreator,
  setMaPeopleListPerPageCreator,
  removeMaPeopleListUserCreator,
  downloadMaPeopleReportCreator,
  setMaPeopleListFiltersCreator,
} from 'store/actionsCreators';
import { ReactSVG } from 'react-svg';
import { iconList } from 'assets';
import { Margin } from 'styled-components-spacing';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Content, Wrapper, Icon, RowStyled, ActionText } from './List.styled';
import { formatDateTime } from 'helpers/date';

const YES_STATUS = 'Yes';

function List({
  items,
  page,
  pages,
  perPage,
  sort,
  loadUsers,
  setSort,
  loading,
  removeUser,
  setPage,
  setPerPage,
  downloadReport,
  filters,
  setFilters,
  localMemberAssociation,
}) {
  const { t } = useTranslation();
  const [removeConfirmModal, setRemoveConfirmModal] = useState(null);
  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    createdFrom: null,
    certified: null,
    membership: null,
    youngCrew: null,
  });

  const simpleOptions = [
    {
      value: 'no',
      label: 'No',
    },
    {
      value: 'yes',
      label: 'Yes',
    },
  ];

  const filtersProps = {
    firstName: {
      type: 'input',
      name: 'first-name',
      id: 'f-name',
      label: t('page.ma.peopleAdd.firstName'),
      placeholder: t('forms.firstNamePlaceholder'),
    },
    lastName: {
      type: 'input',
      name: 'last-name',
      id: 'f-lastName',
      label: t('page.ma.peopleAdd.lastName'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    email: {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.ma.peopleAdd.email'),
      placeholder: t('forms.emailPlaceholder'),
    },
    createdFrom: {
      type: 'datepicker',
      name: 'createdFrom',
      id: 'f-createdFrom',
      label: t('page.ma.peopleAdd.createdFrom'),
      placeholder: t('forms.createdFromPlaceholder'),
    },
    certified: {
      type: 'select',
      name: 'certified',
      id: 'f-jobTitle',
      label: t('page.ma.peopleAdd.certified'),
      placeholder: t('forms.certifiedPlaceholder'),
      options: simpleOptions,
    },
    membership: {
      type: 'select',
      name: 'membership',
      id: 'f-membership',
      label: t('page.ma.peopleAdd.membership'),
      placeholder: t('forms.membershipPlaceholder'),
      options: [
        ...simpleOptions,
        {
          value: 'applied',
          label: 'Applied',
        },
        {
          value: 'join',
          label: 'Join',
        },
        {
          value: 'Rejected',
          label: 'Rejected',
        },
        {
          value: 'MemberRejected',
          label: 'Member Rejected',
        },
      ],
    },
    youngCrew: {
      type: 'select',
      name: 'youngCrew',
      id: 'f-youngCrew',
      label: t('page.ma.peopleAdd.youngCrew'),
      placeholder: t('forms.youngCrewPlaceholder'),
      options: simpleOptions,
    },
  };

  useEffect(() => {
    setPage(1);
  }, [perPage, filters]);

  useEffect(() => {
    loadUsers();
  }, [page, perPage, sort, filters]);

  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = sort || [];
    const sortByNewColumn = currentSortName !== name;

    if (sortByNewColumn) {
      setSort([name, 'desc']);
      return;
    }

    const newSortStatus = currentSortStatus === 'desc' ? 'asc' : 'desc';
    setSort([name, newSortStatus]);
  };

  const handleChangePerPage = ({ value }) => {
    setPerPage(value);
  };

  const isCurrentMa = (user) => {
    return (
      user.memberAssociationPeople &&
      user.memberAssociationPeople.memberAssociation.id === localMemberAssociation
    );
  };

  const getUserStatus = (user) => {
    if (!user.memberAssociationPeople || !isCurrentMa(user)) return 'No';
    return formatStatus(user.memberAssociationPeople.status);
  };

  // Set when the membership was confirmed; empty for members accepted before it was recorded.
  const getMemberSince = (user) => {
    if (!user.memberAssociationPeople || !isCurrentMa(user)) return '-';
    return user.memberAssociationPeople.acceptedDate
      ? moment(user.memberAssociationPeople.acceptedDate).format('DD/MM/YYYY')
      : '-';
  };

  const getYoungCrewStatus = (user) => {
    return user.yCPeople && user.yCPeople.status === 'Yes' ? 'Yes' : 'No';
  };

  const handleAction = (name, id) => () => {
    switch (name) {
      case 'remove':
        return removeUser({ user: id });
      default:
        return console.log('action not defined');
    }
  };

  const downloadButton = () => (
    <Wrapper onClick={() => downloadReport()}>
      <Icon>
        <ReactSVG src={iconList.download} />
      </Icon>
      <Content>
        <p className="word-break">{t('page.ma.peopleList.reportTitle')}</p>
        <Margin top="4">
          <div className="link uppercase">{t('page.ma.peopleList.downloadReport')}</div>
        </Margin>
      </Content>
    </Wrapper>
  );

  const renderRemove = (user) => (
    <>
      {user.memberAssociationPeople && user.memberAssociationPeople.status === YES_STATUS ? (
        <Row>
          <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
            <ButtonAction
              onClick={() => setRemoveConfirmModal(user.id)}
              title={t('global.removeButton')}
              icon="closeCircle"
            />
          </Col>
        </Row>
      ) : null}
    </>
  );

  return (
    <>
      <Container>
        <PageContent>
          <Alerts type="ma" />
          <p dangerouslySetInnerHTML={{ __html: t('page.ma.peopleList.info') }} />
          <Margin
            as="p"
            top={{ xs: 16 }}
            dangerouslySetInnerHTML={{ __html: t('page.ma.peopleList.info2') }}
          />
          <Margin top={{ xs: 16 }}>
            <FiltersCustom
              title={t('page.ma.peopleAdd.filterTitle')}
              filters={filtersProps}
              filterFields={filterFields}
              handleFilterFieldChange={handleFilterFieldChange}
              onFiltersApply={setFilters}
            />
          </Margin>
          <Margin top={{ xs: 16 }}>
            <RowStyled>
              {downloadButton()}
              <FiltersControls perPageOnChange={handleChangePerPage} perPageSelected={perPage} />
            </RowStyled>
          </Margin>
          <Table tablebreakpoint="lg">
            <Table.Head>
              <Table.Row>
                <Table.Heading sort={handleSortBy('firstLastName')}>
                  {t('page.ma.peopleList.firstAndLastName')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('email')}>
                  {t('page.ma.peopleList.email')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('createdAt')}>
                  {t('page.ma.peopleList.createdAt')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('certified')}>
                  {t('page.ma.peopleList.certified')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('status')}>
                  {t('page.ma.peopleList.membershipStatus')}
                </Table.Heading>
                <Table.Heading>{t('page.ma.peopleList.memberSince')}</Table.Heading>
                <Table.Heading>{t('page.ma.peopleList.youngCrewMember')}</Table.Heading>
                <Table.Heading />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !items.length ? (
                <Table.Row>
                  <Table.Cell colSpan={4}>No results found</Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && items.length
                ? items.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.firstAndLastName')}:</Table.Label>
                        <Link to={`/user-profile/${user.uniqueUrl}`}>
                          {`${user.firstName} ${user.lastName}`}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.email')}:</Table.Label>
                        {user.email}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.createdAt')}:</Table.Label>
                        {user.createdAt && formatDateTime(user.createdAt, 'DD/MM/YYYY')}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.certified')}:</Table.Label>
                        {user.certificates.length ? 'Yes' : 'No'}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.membershipStatus')}:</Table.Label>
                        {getUserStatus(user)}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.memberSince')}:</Table.Label>
                        {getMemberSince(user)}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleList.youngCrewMember')}:</Table.Label>
                        {getYoungCrewStatus(user)}
                      </Table.Cell>
                      <Table.Cell>
                        {isCurrentMa(user) ? (
                          <>
                            {user.memberAssociationUserRoles &&
                            user.memberAssociationUserRoles.length > 0 ? (
                              <ActionText>MA Officer</ActionText>
                            ) : (
                              <>{renderRemove(user)}</>
                            )}
                          </>
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  ))
                : null}
            </Table.Body>
          </Table>
          <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
        </PageContent>
      </Container>
      <Modal
        heading={t('page.ma.peopleList.modalDeleteUser')}
        isOpen={!!removeConfirmModal}
        confirm={() => handleAction('remove', removeConfirmModal)() && setRemoveConfirmModal(null)}
        cancel={() => setRemoveConfirmModal(null)}
      />
    </>
  );
}

List.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loadUsers: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  removeUser: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  sort: PropTypes.arrayOf([PropTypes.string, PropTypes.string]),
  downloadReport: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
  localMemberAssociation: PropTypes.number.isRequired,
};
List.defaultProps = {
  sort: [],
};

const mapStateToProps = ({ ma, loading }) => ({
  ...ma.people.list,
  loading: !!loading.filter((item) => item.type === 'MA').length,
});
const mapDispatchToProps = (dispatch) => ({
  loadUsers: () => dispatch(loadMaPeopleListCreator()),
  setSort: (payload) => dispatch(setMaPeopleListSortCreator(payload)),
  removeUser: (payload) => dispatch(removeMaPeopleListUserCreator(payload)),
  setPage: (payload) => dispatch(setMaPeopleListPageCreator(payload)),
  setPerPage: (payload) => dispatch(setMaPeopleListPerPageCreator(payload)),
  downloadReport: () => dispatch(downloadMaPeopleReportCreator('people')),
  setFilters: (payload) => dispatch(setMaPeopleListFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(List);
